// src/hooks/useUploadedMedia.js (or wherever you prefer to put your hooks)
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext"; // Assuming AuthContext is in src/contexts/AuthContext.js

export const useUploadedMedia = () => {
  const [mediaByDivesite, setMediaByDivesite] = useState({});
  const [mediaByDive, setMediaByDive] = useState({});
  const {
    userObjectId,
    currentUser,
    loading: authLoading,
    backendDataLoading: authBackendLoading,
    userClaims, // Added to use for image sizing on frontend
  } = useAuth();
  const [mediaLoading, setMediaLoading] = useState(true);
  const [totalMediaCount, setTotalMediaCount] = useState(0);
  const [divesiteCount, setDivesiteCount] = useState(0);
  const [diveCount, setDiveCount] = useState(0);
  const [likeCounts, setLikeCounts] = useState({});
  const [mediaError, setMediaError] = useState(null); // New state for media-specific errors

  // Helper function to get auth token (memoized)
  const getAuthToken = useCallback(async () => {
    try {
      const authModule = await import("../config/firebase.js"); // Adjust path if needed
      const auth = authModule.default;
      const user = auth.currentUser;
      if (!user) {
        console.warn("useUploadedMedia: No current user found for auth token.");
        return null;
      }
      const token = await user.getIdToken();
      console.log(
        "useUploadedMedia: Auth token successfully retrieved in getAuthToken."
      );
      return token;
    } catch (error) {
      console.error("useUploadedMedia: Error fetching auth token:", error);
      // It's usually better to let the main fetch catch handle loading state if auth fails
      // setMediaError("Failed to authenticate for media fetch.");
      return null;
    }
  }, []); // No dependencies, as auth.currentUser is always up-to-date.

  // Fetch like counts for all media items (memoized)
  const fetchLikeCounts = useCallback(
    async (mediaIds) => {
      if (!mediaIds || mediaIds.length === 0) {
        console.log("useUploadedMedia: No media IDs to fetch like counts for.");
        return;
      }

      try {
        const token = await getAuthToken();
        if (!token) {
          console.warn(
            "useUploadedMedia: Could not get auth token for like counts. Skipping."
          );
          return;
        }
        console.log(
          `useUploadedMedia: Fetching like counts for ${mediaIds.length} items.`
        );
        const response = await axios.post(
          "/api/likes/counts",
          { itemIds: mediaIds },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log(
          "useUploadedMedia: API response for like counts:",
          response.data
        );
        if (response.data?.success) {
          setLikeCounts(response.data.data || {});
          console.log(
            "useUploadedMedia: Like counts set:",
            response.data.data || {}
          );
        } else {
          console.warn(
            "useUploadedMedia: Like counts API response indicated no success or missing data."
          );
          // Set error if API explicitly says no success, but don't stop main loading
          // setMediaError("Failed to fetch like counts.");
        }
      } catch (error) {
        console.error("useUploadedMedia: Error fetching like counts:", error);
        // Set error if network fails, but don't stop main loading
        // setMediaError("Failed to fetch like counts due to network error.");
      }
    },
    [getAuthToken]
  );

  useEffect(() => {
    const fetchUploadedMediaData = async () => {
      console.log(
        "useUploadedMedia: useEffect triggered for media data fetch."
      );
      setMediaError(null); // Always clear previous errors when a new fetch cycle begins

      // --- CRITICAL CHANGE START ---
      // If auth/user data isn't ready or userObjectId is missing,
      // clear data and set loading to false to indicate "not available" or "no user".
      if (authLoading || authBackendLoading || !userObjectId) {
        console.log(
          `useUploadedMedia: Skipping media data fetch. Auth loading: ${authLoading}, Backend data loading: ${authBackendLoading}, User object ID present: ${!!userObjectId}`
        );
        // Clear any existing media data if the user logs out or ID changes, or auth isn't complete
        setMediaByDivesite({});
        setMediaByDive({});
        setTotalMediaCount(0);
        setDivesiteCount(0);
        setDiveCount(0);
        setLikeCounts({});

        // Set loading to false *only if* auth is done and there's no userObjectId
        // This prevents being stuck in a loading state if no user is logged in
        if (!authLoading && !authBackendLoading && !userObjectId) {
          setMediaLoading(false);
        } else {
          // If auth is still loading, keep mediaLoading true to reflect "waiting for auth"
          setMediaLoading(true);
        }
        return; // Exit early, as we can't proceed with the fetch
      }
      // --- CRITICAL CHANGE END ---

      // If we reach here, it means auth and userObjectId are ready, so we can start fetching.
      setMediaLoading(true); // Explicitly set loading to true just before the API call
      console.log(
        `useUploadedMedia: Starting media data fetch for userObjectId: ${userObjectId}`
      );
      try {
        const token = await currentUser.getIdToken();
        console.log(
          "useUploadedMedia: Auth token obtained for media data fetch."
        );

        const response = await axios.get(
          `/api/media/user/${userObjectId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // ADD THESE DEBUG LINES:
        console.log("=== BACKEND RESPONSE DEBUG ===");
        console.log(
          "Full response.data:",
          JSON.stringify(response.data, null, 2)
        );
        console.log("First item structure:", response.data[0]);
        console.log("First item mediaUrl:", response.data[0]?.mediaUrl);
        console.log(
          "Available sizes in first item:",
          Object.keys(response.data[0]?.mediaUrl || {})
        );
        console.log("=== END DEBUG ===");

        console.log(
          "useUploadedMedia: API response for media by user:",
          response.data
        );
        setTotalMediaCount(response.data.length);

        const organizedByDivesite = response.data.reduce((acc, media) => {
          // Ensure media.divesite exists before trying to access its properties
          if (media.divesite) {
            const divesiteName =
              media.divesite?.uddf?.divesite?.site?.name || "Unknown Divesite";
            acc[divesiteName] = acc[divesiteName] || [];
            acc[divesiteName].push(media);
          } else {
            console.warn(
              `useUploadedMedia: Media item ${media._id} has no divesite reference.`
            );
          }
          return acc;
        }, {});

        const organizedByDive = response.data.reduce((acc, media) => {
          // Ensure media.dive exists before trying to access its properties
          if (media.dive) {
            const diveName =
              media.dive?.name ||
              `Dive #${media.dive?._id?.substring(0, 8)}` ||
              "Unknown Dive";
            acc[diveName] = acc[diveName] || [];
            acc[diveName].push(media);
          } else {
            console.warn(
              `useUploadedMedia: Media item ${media._id} has no dive reference.`
            );
          }
          return acc;
        }, {});

        setMediaByDivesite(organizedByDivesite);
        setMediaByDive(organizedByDive);
        setDivesiteCount(Object.keys(organizedByDivesite).length);
        setDiveCount(Object.keys(organizedByDive).length);
        console.log(
          `useUploadedMedia: Divesite count: ${
            Object.keys(organizedByDivesite).length
          }, Dive count: ${Object.keys(organizedByDive).length}`
        );

        const mediaIds = response.data.map((media) => media._id);
        console.log(
          "useUploadedMedia: Fetching like counts for media IDs:",
          mediaIds
        );
        await fetchLikeCounts(mediaIds); // This call's errors are handled internally by fetchLikeCounts
      } catch (error) {
        console.error(
          "useUploadedMedia: Error fetching uploaded media:",
          error
        );
        setMediaError("Failed to load your uploaded media.");
      } finally {
        setMediaLoading(false); // This always runs after the try/catch block
        console.log("useUploadedMedia: Media loading set to false.");
      }
    };

    fetchUploadedMediaData();
  }, [
    userObjectId,
    currentUser,
    authLoading,
    authBackendLoading,
    fetchLikeCounts, // Ensure stable reference (useCallback)
  ]);

  // Determine the actual image size being served by the backend based on user claims
  const getClaimedImageSize = useCallback(() => {
    if (userClaims) {
      if (userClaims.isTier5User) return "large";
      if (userClaims.isTier3User || userClaims.isTier4User) return "medium";
      if (userClaims.isTier1User || userClaims.isTier2User) return "small";
    }
    return "small"; // Default if no claims or lowest tier
  }, [userClaims]); // Depends on userClaims from useAuth

  return {
    mediaByDivesite,
    mediaByDive,
    mediaLoading,
    totalMediaCount,
    divesiteCount,
    diveCount,
    likeCounts,
    mediaError,
    getClaimedImageSize, // Expose this helper for the UI component
  };
};
